<template>
  <div class="pp-page">
    <template v-if="property">
      <!-- Signed-in visitors see this instead of the cached guest view until their own data is in;
           the rules that switch it are in app.vue (they must apply before hydration). -->
      <ProjectPageSkeleton
        v-if="!revealSettled"
        class="signed-in-boot-show"
      />
      <div :class="{ 'signed-in-boot-hide': !revealSettled }">
        <PropertyPageShell :property="property" />
      </div>
    </template>

    <ProjectPageSkeleton v-else-if="revealPending" />

    <div
      v-else
      class="pp-state empty"
    >
      <h2>{{ t('public.projectNotFound.title') }}</h2>
      <p>{{ t('public.projectNotFound.description') }}</p>
      <NuxtLink
        :to="'/explore'"
        class="cta"
        ><span
          >{{ t('public.projectNotFound.browse') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        ></NuxtLink
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { usePropertySeo } from '@/composables/usePropertySeo';
import { useRecordProjectView } from '@/composables/useRecordProjectView';
import PropertyPageShell from '@/components/property/PropertyPageShell.vue';
import ProjectPageSkeleton from '@/components/property/ProjectPageSkeleton.vue';

definePageMeta({
  layout: 'default',
});

const { t } = useI18n();

const route = useRoute();
const authStore = useAuthStore();
const developerSlug = computed(() => String(route.params.developer || '').toLowerCase());
const slug = computed(() => String(route.params.slug || '').toLowerCase());

type SlugPropertyResponse = {
  success: boolean;
  data?: Record<string, unknown> & { id?: string; slug?: string; developerSlug?: string };
};

const apiPath = computed(() => `/api/properties/by-developer-slug/${developerSlug.value}/${slug.value}`);

// ── PUBLIC fetch (SSR + hydration) — NEVER sends the auth token ──────────
// getAccessLevel() on the server reads ONLY the Authorization header (it
// ignores cookies), and browsers don't send an Authorization header on a
// document request — so omitting it here guarantees the server returns the
// redacted public view (fuzzy `apartments_summary` bands, masked contacts).
//
// This is deliberate and load-bearing: the page is SWR-cached in prod, and
// the cached HTML is served to every visitor regardless of who warmed it.
// Keeping the SSR render user-independent means (a) crawlers/guests get the
// correct public view, and (b) an agent's commission / contacts / full unit
// availability can NEVER be baked into the cached HTML and leaked to guests.
const { data: publicData } = await useFetch<SlugPropertyResponse>(apiPath, {
  key: computed(() => `property-public-${developerSlug.value}-${slug.value}`),
});

// ── AUTHED reveal — CLIENT ONLY ─────────────────────────────────────────
// Runs on mount and whenever the token or slug changes. Crucially this is
// NOT gated on the token *changing* (the old `watch: [token]` never fired
// when the token was already present at hydration, so on prod — where SWR
// served a cached guest render — verified agents were stuck on the public
// view with no availability). We always re-fetch with the header client-side
// to reveal the agent's full apartments + verified contacts + commission.
const authedData = ref<SlugPropertyResponse | null>(null);
// Catalogue-only exclusives are agent-only, so the guest fetch 404s; hold the "not available" state until the
// agent's own fetch settles. False on the server, so the cached SSR HTML stays user-independent.
const revealing = ref(false);
const mounted = ref(false);
// False through SSR and hydration, so the server HTML and the first client render match.
const revealSettled = ref(false);
const revealAuthed = async () => {
  if (!import.meta.client || !authStore.token) {
    authedData.value = null;
    revealSettled.value = true;
    return;
  }
  revealing.value = true;
  try {
    authedData.value = await $fetch<SlugPropertyResponse>(apiPath.value, {
      headers: { Authorization: `Bearer ${authStore.token}` },
    });
  } catch {
    authedData.value = null;
  } finally {
    revealing.value = false;
    revealSettled.value = true;
  }
};
onMounted(() => (mounted.value = true));
onMounted(revealAuthed);
watch([() => authStore.token, apiPath], revealAuthed);

const revealPending = computed(() => !mounted.value || revealing.value);

// Authed data wins once it lands; until then (SSR, guests, pre-reveal) the
// public view shows.
const property = computed(() => {
  const authed = authedData.value?.success ? (authedData.value.data ?? null) : null;
  if (authed) return authed;
  return publicData.value?.success ? (publicData.value.data ?? null) : null;
});

usePropertySeo(property);
useRecordProjectView(property);

// Attribute organic signups to the project page that brought the visitor in.
// Guests only (a logged-in user already has an account); first-touch wins and
// the cookie is read server-side at signup. See captureAttribution.ts.
if (import.meta.client) {
  const { recordRegistrationSource } = useTracking();
  watch(
    property,
    (p) => {
      if (authStore.token || !p) return;
      const name = String((p as { title?: { en?: string } }).title?.en || '').trim();
      if (name) {
        recordRegistrationSource({
          type: 'project',
          slug: `${developerSlug.value}/${slug.value}`,
          name,
        });
      }
    },
    { immediate: true },
  );
}
</script>

<style scoped lang="scss">
.pp-page {
  background: white;
  min-height: 100%;
}

.pp-state {
  text-align: center;
  padding: 80px 16px;
  color: rgba(13, 15, 21, 0.62);
  font-size: 14px;

  &.empty {
    h2 {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 28px;
      letter-spacing: -0.02em;
      color: #0d0f15;
      margin: 0 0 8px;
    }

    p {
      max-width: 520px;
      margin: 0 auto 24px;
    }

    .cta {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 44px;
      padding: 0 18px;
      border-radius: 12px;
      background: #ffb648;
      color: #0d0f15;
      font-size: 13px;
      font-weight: 700;
      text-decoration: none;
    }
  }
}
</style>
